import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

import type { NamedResourceAdapter } from "./types";

const namedResourceFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: "Name is required" })
    .max(255, { message: "Name must be 255 characters or fewer" }),
});

type NamedResourceFormValues = z.infer<typeof namedResourceFormSchema>;

export type NamedResourceFormDialogProps<TItem> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  initialItem?: TItem | null;
  adapter: NamedResourceAdapter<TItem>;
};

export function NamedResourceFormDialog<TItem>({
  open,
  onOpenChange,
  mode,
  initialItem,
  adapter,
}: NamedResourceFormDialogProps<TItem>) {
  const form = useForm<NamedResourceFormValues>({
    resolver: zodResolver(namedResourceFormSchema),
    defaultValues: { name: "" },
  });
  const createMutation = adapter.useCreate();
  const updateMutation = adapter.useUpdate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      form.reset({
        name:
          mode === "edit" && initialItem !== undefined && initialItem !== null
            ? adapter.getName(initialItem)
            : "",
      });
      setSubmitError(null);
    }
  }, [open, mode, initialItem, form, adapter]);

  async function onSubmit(values: NamedResourceFormValues) {
    setSubmitError(null);
    try {
      if (mode === "create") {
        await createMutation.mutateAsync({ name: values.name });
      } else if (initialItem !== undefined && initialItem !== null) {
        await updateMutation.mutateAsync({
          id: adapter.getId(initialItem),
          body: { name: values.name },
        });
      }
      onOpenChange(false);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : mode === "create"
            ? `Failed to create ${adapter.singularLabel}. Try again.`
            : `Failed to update ${adapter.singularLabel}. Try again.`,
      );
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const title =
    mode === "create"
      ? `New ${adapter.singularLabel}`
      : `Edit ${adapter.singularLabel}`;
  const submitLabel =
    mode === "create" ? `Create ${adapter.singularLabel}` : "Save changes";
  const pendingLabel = mode === "create" ? "Creating…" : "Saving…";
  const description =
    mode === "create" ? adapter.createDescription : adapter.editDescription;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      autoComplete="off"
                      maxLength={255}
                      placeholder={adapter.placeholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {submitError !== null && (
              <p role="alert" className="text-sm font-medium text-error">
                {submitError}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? pendingLabel : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
